import React, { useState } from 'react';
import { useSubscriptionTrial } from '../../../authentication_components/subscriptionTrialContext';
// 1. NEW IMPORTS: Admin Tracker & Auth
import { useAdminTracker } from '../../../authentication_components/adminTrackerContext';
import { useAuth } from '../../../authentication_components/authContext';
import { Clock, AlertTriangle, CheckCircle, X, ShieldCheck } from 'lucide-react';

export default function SubscriptionTrialDialog({ businessRegistryNumber, businessName, currentTier, onSuccess }) {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('give'); // 'give' or 'cancel'
  const [trialDays, setTrialDays] = useState(7);
  const [selectedTier, setSelectedTier] = useState(currentTier || 'negocio');
  const [confirmRegistry, setConfirmRegistry] = useState('');
  
  const { giveTrial, cancelTrial, loading, error, clearError, VALID_TIERS } = useSubscriptionTrial();
  
  // 2. INITIALIZE TRACKER HOOKS
  const { createTracker } = useAdminTracker();
  const { adminId } = useAuth();
  
  const [successMessage, setSuccessMessage] = useState(null);

  const handleOpen = () => {
    setIsOpen(true);
    setSuccessMessage(null);
    clearError();
    // Default to 'negocio' if current tier isn't standard
    setSelectedTier(VALID_TIERS.includes(currentTier?.toLowerCase()) ? currentTier.toLowerCase() : 'negocio');
  };

  const handleClose = () => {
    setIsOpen(false);
    setSuccessMessage(null);
    clearError();
    setConfirmRegistry('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    setSuccessMessage(null);

    try {
      if (mode === 'give') {
        // ---------------------------------------------------------
        // LOGIC FOR GIVING/EXTENDING TRIAL
        // ---------------------------------------------------------
        const result = await giveTrial(businessRegistryNumber, trialDays, selectedTier);
        setSuccessMessage(result.message);

        // 3. TRACK "GIVE" ACTION (Safe-Fail Pattern)
        try {
          await createTracker({
            admin_id: adminId || 'unknown',
            change_type: 'subscription_trial_grant',
            change_reason: `Business ${businessRegistryNumber} (${businessName}) - Granted/Extended trial: ${selectedTier.toUpperCase()} for ${trialDays} days`
          });
          console.log('[AdminTracker] Trial grant tracked successfully');
        } catch (trackerErr) {
          console.error('[AdminTracker] Failed to track trial grant:', trackerErr);
        }

      } else {
        // ---------------------------------------------------------
        // LOGIC FOR CANCELLING TRIAL
        // ---------------------------------------------------------
        if (confirmRegistry !== businessRegistryNumber) {
            return; 
        }
        const result = await cancelTrial(businessRegistryNumber);
        setSuccessMessage(result.message);

        // 4. TRACK "CANCEL" ACTION (Safe-Fail Pattern)
        try {
          await createTracker({
            admin_id: adminId || 'unknown',
            change_type: 'subscription_cancel_force',
            change_reason: `Business ${businessRegistryNumber} (${businessName}) - Subscription/Trial manually cancelled by admin`
          });
          console.log('[AdminTracker] Cancellation tracked successfully');
        } catch (trackerErr) {
          console.error('[AdminTracker] Failed to track cancellation:', trackerErr);
        }
      }

      // Close after short delay or let user close
      setTimeout(() => {
        if (onSuccess) onSuccess(); // Refresh parent data
      }, 1500);

    } catch (err) {
      console.error(err);
      // Note: We don't track failures here to avoid database spam, 
      // but you could add an 'action_failed' log here if strictly required.
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={handleOpen}
        className="ml-auto flex items-center px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-md text-sm font-medium"
      >
        <ShieldCheck className="h-4 w-4 mr-2" />
        Gestionar Trial
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-orange-100">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-50 to-orange-100 px-6 py-4 border-b border-orange-200 flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800 flex items-center">
            <Clock className="h-5 w-5 text-orange-500 mr-2" />
            Gestionar Periodo de Prueba
          </h3>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Mode Selection Tabs */}
          <div className="flex bg-gray-100 p-1 rounded-lg mb-6">
            <button
              onClick={() => { setMode('give'); clearError(); setSuccessMessage(null); }}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                mode === 'give' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Otorgar / Extender Trial
            </button>
            <button
              onClick={() => { setMode('cancel'); clearError(); setSuccessMessage(null); }}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                mode === 'cancel' ? 'bg-white text-red-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Cancelar Trial
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertTriangle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-700">{successMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            
            {mode === 'give' ? (
              <>
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 mb-4">
                    <p className="text-xs text-blue-700">
                        <strong>Nota:</strong> Si el negocio tiene una suscripción activa, los días se sumarán a la fecha de vencimiento actual.
                    </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Negocio</label>
                  <input 
                    type="text" 
                    value={businessName} 
                    disabled 
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Días de Prueba</label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={trialDays}
                    onChange={(e) => setTrialDays(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nivel (Tier)</label>
                  <select
                    value={selectedTier}
                    onChange={(e) => setSelectedTier(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none capitalize"
                  >
                    {VALID_TIERS.map(tier => (
                        <option key={tier} value={tier}>{tier}</option>
                    ))}
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={loading || !!successMessage}
                  className="w-full mt-4 bg-orange-600 text-white py-2 rounded-lg font-medium hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center"
                >
                  {loading ? (
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  ) : (
                    'Confirmar Trial'
                  )}
                </button>
              </>
            ) : (
              <>
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 mb-4">
                  <h4 className="text-sm font-bold text-red-800 mb-1">¡Acción Destructiva!</h4>
                  <p className="text-xs text-red-600">
                    Esto terminará el periodo de prueba inmediatamente. Si el usuario no tiene un método de pago válido para renovar, la suscripción podría cancelarse.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Escribe el # de Registro para confirmar:
                    <span className="block text-xs font-mono text-gray-500 mt-1">{businessRegistryNumber}</span>
                  </label>
                  <input
                    type="text"
                    value={confirmRegistry}
                    onChange={(e) => setConfirmRegistry(e.target.value)}
                    placeholder={businessRegistryNumber}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:outline-none"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || confirmRegistry !== businessRegistryNumber || !!successMessage}
                  className="w-full mt-4 bg-red-600 text-white py-2 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center"
                >
                  {loading ? (
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  ) : (
                    'Cancelar Trial Ahora'
                  )}
                </button>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}